import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { formatINR, formatDateTime, waLink } from "../lib/format";
import { toast } from "sonner";
import { Phone, MessageCircle, Plus, UserCheck, Share2, FileText, Archive as ArchiveIcon, Pencil } from "lucide-react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { openAuthDownload } from "../lib/download";
import TrustScoreCard from "../components/TrustScoreCard";

export default function CustomerDetail() {
    const { id } = useParams();
    const { t } = useLang();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [c, setC] = useState(null);
    const [loans, setLoans] = useState([]);
    const [logs, setLogs] = useState([]);
    const [users, setUsers] = useState([]);
    const [note, setNote] = useState("");

    async function load() {
        const [a, b, d] = await Promise.all([
            api.get(`/customers/${id}`),
            api.get(`/customers/${id}/loans`),
            api.get(`/customers/${id}/call-logs`),
        ]);
        setC(a.data);
        setLoans(b.data);
        setLogs(d.data);
    }
    useEffect(() => {
        load();
        if (isAdmin) api.get("/users").then((r) => setUsers(r.data));
        // eslint-disable-next-line
    }, [id]);

    async function assign(username) {
        try {
            await api.put(`/customers/${id}/assign`, { assigned_to: username === "none" ? null : username });
            toast.success("Assignment updated");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function setCustomerAuth() {
        const adminPwd = prompt("Confirm your ADMIN password to continue:");
        if (!adminPwd) return;
        const kind = prompt(
            "Which credential to set/reset?\n\n" +
            "Type 'p' for temporary Password\n" +
            "Type 'n' for temporary PIN\n" +
            "Type 'b' for both"
        );
        if (!kind) return;
        const k = kind.trim().toLowerCase();
        const payload = { admin_password_confirm: adminPwd };
        if (k === "p" || k === "b") {
            const pw = prompt("Enter temporary PASSWORD (6–128 characters):");
            if (!pw) return;
            payload.temp_password = pw;
        }
        if (k === "n" || k === "b") {
            const pin = prompt("Enter temporary PIN (4–6 digits):");
            if (!pin) return;
            payload.temp_pin = pin;
        }
        if (!payload.temp_password && !payload.temp_pin) {
            toast.error("Please choose password, PIN or both.");
            return;
        }
        try {
            const { data } = await api.post(`/customers/${id}/set-customer-auth`, payload);
            const bits = [];
            if (data.password_set) bits.push("temporary password");
            if (data.pin_set) bits.push("temporary PIN");
            toast.success(
                `Set ${bits.join(" and ")}. Customer must change on first login.`,
                { duration: 6000 }
            );
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function setCustomerRecovery() {
        const adminPwd = prompt("Confirm your ADMIN password to continue:");
        if (!adminPwd) return;
        const dob = prompt("Customer's Date of Birth (YYYY-MM-DD):");
        if (!dob) return;
        const question = prompt("Security question (e.g. \"Mother's maiden name?\"):");
        if (!question) return;
        const answer = prompt("Security answer (will be hashed, case-insensitive):");
        if (!answer) return;
        try {
            await api.post(`/customers/${id}/set-security-question`, {
                admin_password_confirm: adminPwd,
                dob,
                question,
                answer,
            });
            toast.success("Recovery details saved. Customer can now use Forgot Password / PIN.", { duration: 5000 });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function sharePortalLink() {
        try {
            const { data } = await api.post(`/customers/${id}/portal-link`, { days: 30 });
            const portalUrl = `${window.location.origin}/portal?t=${data.token}`;
            const msg =
                `Hi ${data.customer_name}, here's your secure customer portal link:\n${portalUrl}\n\n` +
                `You can view your loan balance, payment history and download passbook anytime. ` +
                `Link valid 30 days. — Lokdhara Microfinance`;
            const wa = waLink(data.customer_mobile, msg);
            window.open(wa, "_blank", "noopener,noreferrer");
            // Copy to clipboard as a backup
            if (navigator?.clipboard) {
                try {
                    await navigator.clipboard.writeText(portalUrl);
                } catch (clipErr) {
                    console.warn("Clipboard write failed:", clipErr);
                }
            }
            toast.success("Portal link generated & copied");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function addLog(e) {
        e.preventDefault();
        if (!note.trim()) return;
        try {
            await api.post(`/customers/${id}/call-log`, { notes: note });
            setNote("");
            toast.success("Call note saved");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    if (!c) return <div className="text-slate-500">Loading…</div>;

    const initials = (c.name || "U")
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();

    return (
        <div className="space-y-5">
            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6">
                <div className="flex items-start gap-4 flex-wrap">
                    {c.photo ? (
                        <img
                            src={c.photo}
                            alt={c.name}
                            className="h-20 w-20 rounded-2xl object-cover border border-slate-200"
                        />
                    ) : (
                        <Avatar className="h-20 w-20 rounded-2xl">
                            <AvatarFallback className="bg-blue-900 text-white text-xl rounded-2xl">
                                {initials}
                            </AvatarFallback>
                        </Avatar>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-display text-2xl font-bold text-slate-900">{c.name}</h1>
                            <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                {c.customer_code}
                            </span>
                        </div>
                        <div className="text-sm text-slate-500 mt-1">
                            {c.occupation || "—"} · {c.address || "—"}
                        </div>
                        <div className="mt-3 flex gap-2 flex-wrap">
                            <a
                                href={`tel:${c.mobile}`}
                                data-testid="call-customer-btn"
                                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-blue-900 text-white text-sm font-semibold tap"
                            >
                                <Phone className="h-4 w-4" /> {c.mobile}
                            </a>
                            <a
                                href={waLink(c.mobile, `Hi ${c.name}, this is Lokdhara Microfinance.`)}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="wa-customer-btn"
                                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold tap"
                            >
                                <MessageCircle className="h-4 w-4" /> WhatsApp
                            </a>
                            {isAdmin && (
                                <Link to={`/customers/${c.id}/edit`}>
                                    <Button
                                        variant="outline"
                                        data-testid="edit-customer-btn"
                                        className="h-10 rounded-xl tap font-semibold text-slate-700"
                                    >
                                        <Pencil className="h-4 w-4 mr-1" />
                                        Edit
                                    </Button>
                                </Link>
                            )}
                            {isAdmin && (
                                <Link to={`/loans/new?customer=${c.id}`}>
                                    <Button
                                        data-testid="new-loan-btn"
                                        className="h-10 rounded-xl bg-slate-900 hover:bg-slate-800 tap"
                                    >
                                        <Plus className="h-4 w-4 mr-1" />
                                        {t("add_loan")}
                                    </Button>
                                </Link>
                            )}
                            {isAdmin && (
                                <Button
                                    onClick={sharePortalLink}
                                    data-testid="share-portal-btn"
                                    className="h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white tap"
                                >
                                    <Share2 className="h-4 w-4 mr-1" />
                                    Share Portal Link
                                </Button>
                            )}
                            {isAdmin && (
                                <Button
                                    onClick={setCustomerAuth}
                                    data-testid="set-customer-auth-btn"
                                    variant="outline"
                                    className="h-10 rounded-xl tap"
                                >
                                    <UserCheck className="h-4 w-4 mr-1" />
                                    Set / Reset Customer Login
                                </Button>
                            )}
                            {isAdmin && (
                                <Button
                                    onClick={setCustomerRecovery}
                                    data-testid="set-customer-recovery-btn"
                                    variant="outline"
                                    className="h-10 rounded-xl tap"
                                >
                                    <UserCheck className="h-4 w-4 mr-1" />
                                    Set Recovery (DOB + Question)
                                </Button>
                            )}
                            {isAdmin && (
                                <Button
                                    variant="outline"
                                    onClick={() => openAuthDownload(`/customers/${c.id}/profile-pdf`)}
                                    data-testid="profile-pdf-btn"
                                    className="h-10 rounded-xl tap"
                                >
                                    <FileText className="h-4 w-4 mr-1" />
                                    Profile PDF
                                </Button>
                            )}
                            {isAdmin && !c.archived && (
                                <Button
                                    variant="outline"
                                    onClick={async () => {
                                        const reason = prompt("Reason for archiving (optional):") ?? "";
                                        try {
                                            await api.post(`/customers/${c.id}/archive`, { reason });
                                            toast.success("Archived");
                                            window.location.href = "/customers";
                                        } catch (e) {
                                            toast.error(formatApiError(e));
                                        }
                                    }}
                                    data-testid="archive-customer-btn"
                                    className="h-10 rounded-xl text-amber-700 border-amber-200 hover:bg-amber-50 tap"
                                >
                                    <ArchiveIcon className="h-4 w-4 mr-1" />
                                    Archive
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mt-5 pt-5 border-t border-slate-100">
                    <Info label={t("monthly_income")} value={c.monthly_income ? formatINR(c.monthly_income) : "—"} />
                    <Info label={t("alt_mobile")} value={c.alt_mobile || "—"} />
                </div>

                <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-slate-700">
                        <UserCheck className="h-4 w-4 text-blue-900" />
                        <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                            Assigned to
                        </span>
                    </div>
                    {isAdmin ? (
                        <Select value={c.assigned_to || "none"} onValueChange={assign}>
                            <SelectTrigger className="h-9 rounded-lg w-64" data-testid="assign-customer-select">
                                <SelectValue placeholder="Unassigned" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Unassigned (creator: {c.created_by || "—"})</SelectItem>
                                {users.map((u) => (
                                    <SelectItem key={u.id} value={u.username}>
                                        {u.name} ({u.username})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Badge
                            data-testid="assigned-to-badge"
                            className="bg-blue-50 text-blue-900 border-blue-100 hover:bg-blue-50"
                        >
                            {c.assigned_to || c.created_by || "—"}
                        </Badge>
                    )}
                </div>
            </Card>

            {isAdmin && <TrustScoreCard customerId={id} />}

            <Tabs defaultValue="loans">
                <TabsList className="bg-white border border-slate-100 rounded-xl">
                    <TabsTrigger value="loans" data-testid="tab-loans">
                        {t("loan_history")}
                    </TabsTrigger>
                    {isAdmin && (
                        <TabsTrigger value="kyc" data-testid="tab-kyc">
                            KYC
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="calls" data-testid="tab-calls">
                        {t("call_log")}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="loans" className="mt-4">
                    <Card className="rounded-2xl border-slate-100 overflow-hidden">
                        {loans.length === 0 ? (
                            <div className="p-8 text-center text-sm text-slate-400">{t("no_data")}</div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {loans.map((l) => (
                                    <Link
                                        to={`/loans/${l.id}`}
                                        key={l.id}
                                        className="block p-4 hover:bg-slate-50"
                                        data-testid={`loan-row-${l.id}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-semibold text-slate-900">
                                                    {l.loan_code} · {l.loan_type}
                                                </div>
                                                <div className="text-xs text-slate-500">
                                                    {l.interest_rate}% / mo · {l.tenure_months} months
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="num font-bold">
                                                    {formatINR(l.outstanding_principal)}
                                                </div>
                                                <Badge
                                                    className={
                                                        l.status === "active"
                                                            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                                            : "bg-slate-100 text-slate-700"
                                                    }
                                                >
                                                    {l.status}
                                                </Badge>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="kyc" className="mt-4">
                    {isAdmin && (
                        <Card className="rounded-2xl border-slate-100 p-5 grid sm:grid-cols-3 gap-4">
                            <KycDoc label={t("photo")} value={c.photo} />
                            <KycDoc label={t("aadhaar")} value={c.aadhaar} />
                            <KycDoc label={t("pan")} value={c.pan} />
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="calls" className="mt-4 space-y-4">
                    <Card className="rounded-2xl border-slate-100 p-5">
                        <form onSubmit={addLog} className="space-y-2">
                            <Textarea
                                data-testid="call-note-input"
                                placeholder={t("add_call_note")}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={2}
                                className="rounded-xl"
                            />
                            <Button
                                type="submit"
                                data-testid="save-call-note-btn"
                                className="h-10 rounded-xl bg-blue-900 hover:bg-blue-800"
                            >
                                {t("save_note")}
                            </Button>
                        </form>
                    </Card>
                    <Card className="rounded-2xl border-slate-100">
                        {logs.length === 0 ? (
                            <div className="p-6 text-center text-sm text-slate-400">{t("no_data")}</div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {logs.map((l) => (
                                    <div key={l.id} className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs text-slate-500">
                                                {formatDateTime(l.called_at)} · {l.staff_name}
                                            </div>
                                        </div>
                                        <div className="text-sm text-slate-800 mt-1">{l.notes}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                {label}
            </div>
            <div className="text-sm font-semibold text-slate-900 mt-0.5">{value}</div>
        </div>
    );
}

function KycDoc({ label, value }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
                {label}
            </div>
            {value ? (
                <a href={value} target="_blank" rel="noreferrer">
                    <img
                        src={value}
                        alt={label}
                        className="w-full h-32 object-cover rounded-xl border border-slate-200"
                    />
                </a>
            ) : (
                <div className="w-full h-32 rounded-xl border border-dashed border-slate-200 grid place-items-center text-slate-400 text-xs">
                    Not uploaded
                </div>
            )}
        </div>
    );
}
